import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=not-authed, object=authed
  const [error, setError] = useState("");

  const fetchMe = useCallback(async () => {
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        return;
      } catch (e) {
        const status = e?.response?.status;
        if (status && status < 500) { setUser(false); return; }
        await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
      }
    }
    setUser(false);
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.token) localStorage.setItem("cq_token", data.token);
      setUser(data);
      return data;
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("cq_token");
    setUser(false);
  };

  const changePassword = async (current_password, new_password) => {
    await api.post("/auth/change-password", { current_password, new_password });
    setUser((u) => (u ? { ...u, must_change_password: false } : u));
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, changePassword, error, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
